// ./src/hooks/useDashboard.ts
import { useState, useCallback, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { Dashboard, DashboardInput, DashboardService } from "@/utils/api";

export interface UseDashboardOptions {
  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;

  /** Number of recent activity items to fetch (default: 10, max: 25) */
  activityLimit?: number;

  /** Number of top forms to fetch (default: 5, max: 10) */
  topFormsLimit?: number;
}

export interface UseDashboardReturn {
  // Data state
  dashboard: Dashboard | null;
  loading: boolean;

  // Convenience accessors
  planUsage: Dashboard["planUsage"] | null;
  stats: Dashboard["stats"] | null;
  recentActivity: Dashboard["recentActivity"];
  topForms: Dashboard["topForms"];
  alerts: Dashboard["alerts"];
  onboarding: Dashboard["onboarding"] | null;
  generatedAt: string | null;

  // Operations
  get: (params?: DashboardInput) => Promise<Dashboard | null>;
  refresh: () => Promise<void>;

  // State management
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

export const useDashboard = (
  options: UseDashboardOptions = {}
): UseDashboardReturn => {
  const {
    autoLoad = true,
    userId = null,
    activityLimit = 10,
    topFormsLimit = 5,
  } = options;

  // Use refs for option values to avoid dependency issues
  const activityLimitRef = useRef(activityLimit);
  const topFormsLimitRef = useRef(topFormsLimit);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    activityLimitRef.current = activityLimit;
  }, [activityLimit]);

  useEffect(() => {
    topFormsLimitRef.current = topFormsLimit;
  }, [topFormsLimit]);

  // Data state
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setDashboard(null);
    setLastError(null);
    setLoading(false);
  }, []);

  // Get dashboard data
  const get = useCallback(
    async (params?: DashboardInput): Promise<Dashboard | null> => {
      setLoading(true);
      setLastError(null);

      try {
        const mergedParams: DashboardInput = {
          activityLimit: activityLimitRef.current,
          topFormsLimit: topFormsLimitRef.current,
          ...params,
        };

        const result = await DashboardService.get(mergedParams);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = result.data[0] as Dashboard;
          setDashboard(data);
          setLastError(null);
          return data;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load dashboard";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load dashboard";
        setLastError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Refresh dashboard
  const refresh = useCallback(async () => {
    await get();
  }, [get]);

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        get();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        get();
      }
    }
  }, [userId, autoLoad, get, reset]);

  return {
    // Data state
    dashboard,
    loading,

    // Convenience accessors
    planUsage: dashboard?.planUsage ?? null,
    stats: dashboard?.stats ?? null,
    recentActivity: dashboard?.recentActivity ?? [],
    topForms: dashboard?.topForms ?? [],
    alerts: dashboard?.alerts ?? [],
    onboarding: dashboard?.onboarding ?? null,
    generatedAt: dashboard?.generatedAt ?? null,

    // Operations
    get,
    refresh,

    // State management
    reset,

    // Error handling
    lastError,
    clearError,
  };
};
